import { isRecord } from '@/utils/objects';
import { isEmptyString } from '@/utils/string';
import { CHANNEL_LOG_TYPE_NAMES } from './channel-log-type-names';
import type { ActivityLog } from './types';

/**
 * Activity-log `type` wire values referenced in branching logic (object/relationship types in
 * their collapsed `_o:*:` form). The label/group tables below are still keyed by the raw strings.
 */
export const LOG_TYPE = {
  Event: 'event',
  Geofence: 'geofence',
  Consent: 'consent',
  AttributeChange: 'attribute_change',
  FailedAttributeChange: 'failed_attribute_change',
  ObjectAttributeChange: '_o:*:attribute_change',
  ObjectFailedAttributeChange: '_o:*:failed_attribute_change',
  ProfileCreate: 'profile_create',
  ObjectCreate: '_o:*:create',
  ProfileDelete: 'profile_delete',
  ObjectDelete: '_o:*:delete',
  ProfileMerge: 'profile_merge',
  SegmentBuild: 'segment_build',
  SegmentMembershipChange: 'profile_segment_membership_change',
  WebhookEvent: 'webhook_event',
  AnonMerge: 'anon_merge',
  FailedLlmAction: 'failed_llm_action',
  FailedQueryObjects: 'failed_query_objects',
  ObjectAddRelationship: '_o:*:add_relationship',
  ObjectDeleteRelationship: '_o:*:delete_relationship',
  RelationshipAttributeChange: '_r:*:attribute_change',
  RelationshipFailedAttributeChange: '_r:*:failed_attribute_change',
} as const;

/** The log types recorded against an object (Ember `LOG_TYPES_FOR_OBJECTS`). */
export const LOG_TYPES_FOR_OBJECTS: readonly string[] = [
  LOG_TYPE.ObjectAttributeChange,
  LOG_TYPE.ObjectFailedAttributeChange,
  LOG_TYPE.ObjectCreate,
  LOG_TYPE.ObjectDelete,
  LOG_TYPE.ObjectAddRelationship,
  LOG_TYPE.ObjectDeleteRelationship,
];

/** The log types recorded against a relationship's attributes (Ember `LOG_TYPES_FOR_RELATIONSHIPS_ATTRIBUTES`). */
export const LOG_TYPES_FOR_RELATIONSHIPS_ATTRIBUTES: readonly string[] = [
  LOG_TYPE.RelationshipAttributeChange,
  LOG_TYPE.RelationshipFailedAttributeChange,
];

/**
 * Human labels for activity-log `type` values, keyed by the parsed type (object/relationship
 * types are collapsed to their `_o:*:` / `_r:*:` form first). Channel-specific labels come from
 * `CHANNEL_LOG_TYPE_NAMES`; a channel-native type without an entry falls back to its raw value.
 */
const LOG_TYPE_NAMES: Record<string, string> = {
  anon_merge: 'Anonymous data merged',
  attempted_action: 'Attempted to send Action',
  attribute_change: 'Attribute change',
  bounced_action: 'Bounced Action',
  clicked_action: 'Clicked Action',
  clicked_content: 'Clicked content',
  consent: 'Consent record',
  converted_action: 'Converted Action',
  converted_content: 'Converted content',
  deferred_action: 'Deferred Action',
  delivered_action: 'Delivered Action',
  device_change: 'Device change',
  drafted_action: 'Drafted Action',
  dropped_action: 'Suppressed Action',
  event: 'Event',
  failed_action: 'Failed to send Action',
  failed_attribute_change: 'Failed attribute change',
  failed_batch_update: 'Failed batch update',
  failed_event: 'Failed event',
  failed_object_journeys: 'Failed Journeys for Object/Relationship Campaign',
  failed_query_collection: 'Failed collection query',
  failed_query_objects: 'Failed object query',
  geofence: 'Geofence',
  '_o:*:attribute_change': 'Object attribute change',
  '_o:*:create': 'Object created',
  '_o:*:delete': 'Object deleted',
  '_o:*:failed_attribute_change': 'Object failed attribute change',
  '_o:*:add_relationship': 'Relationship Created (to Person)',
  '_o:*:delete_relationship': 'Relationship Deleted (to Person)',
  opened_action: 'Opened Action',
  page: 'Page view',
  add_relationship: 'Relationship created (to object)',
  delete_relationship: 'Relationship deleted (to object)',
  profile_create: 'Person Created',
  profile_delete: 'Person Deleted',
  profile_merge: 'People Merged',
  '_r:*:attribute_change': 'Relationship attribute change',
  '_r:*:failed_attribute_change': 'Relationship failed attribute change',
  screen: 'Screen view',
  profile_segment_membership_change: 'Segment membership change',
  segment_build: 'Build segment',
  sent_action: 'Sent Action',
  skipped_update: 'Skipped update',
  spammed_action: 'Spammed Action',
  replied_action: 'Replied to Action',
  undeliverable_action: 'Undeliverable Action',
  unsubscribed_action: 'Unsubscribed Action',
  viewed_content: 'Viewed content',
  webhook_event: 'Webhook arrived',
  inbound: 'Inbound message',
  failed_llm_action: 'Failed LLM',
};

/** Product-noun terms the dynamic labels interpolate (resolved via the taxonomy service). */
export interface ActivityLogTypeTerms {
  campaignSingular: string;
  personSingular: string;
  personPlural: string;
}

/**
 * Delivery-type → channel display name, used to replace the word "Action" in `*_action` labels
 * (e.g. "Sent Action" → "Sent Email"). Covers the common channels; white-label / less-common
 * delivery types fall back to the generic "Action".
 */
const DELIVERY_TYPE_NAMES: Record<string, string> = {
  email: 'email',
  push: 'push notification',
  sms: 'SMS',
  slack: 'Slack',
  webhook: 'webhook',
  twilio: 'SMS',
  whatsapp: 'WhatsApp',
  line: 'LINE',
  in_app: 'in-app',
  letter: 'letter',
  live_notification: 'live notification',
};

/**
 * A sent live-notification delivery is a lifecycle operation (its `attrs.operation`), so the row
 * label leads with the operation rather than the generic "Sent". Matching `sent_action` alone is
 * exhaustive: the backend types delivery events as `<metric>_action` (email is the lone
 * channel-native exception), so `sent_live_notification` occurs only as a filter token, never as
 * a row type.
 */
const LIVE_NOTIFICATION_OPERATION_LABELS: Record<string, string> = {
  start: 'Started live notification',
  update: 'Updated live notification',
  end: 'Ended live notification',
};

const OBJECT_TYPE_SEGMENT = /^(_(o|r)):([^:]+):/;

/** Collapse object/relationship types to their wildcard form (`_o:<id>:x` → `_o:*:x`). */
export function parseLogType(type: string): string {
  return type.replace(/(_(o|r)):[^:]+:/, '$1:*:');
}

/** Pin a wildcard object/relationship type to one object type (`_o:*:x` → `_o:<id>:x`). */
export function logTypeForObjectType(type: string, objectTypeId: string): string {
  return type.replace(OBJECT_TYPE_SEGMENT, `$1:${objectTypeId}:`);
}

/** The object type an object/relationship type names, or null for a wildcard or any other type. */
export function objectTypeIdOfLogType(type: string): string | null {
  const id = OBJECT_TYPE_SEGMENT.exec(type)?.[3];
  return id == null || id === '*' ? null : id;
}

/**
 * A non-anon-merge log whose sources carry an anonymous_id (i.e. an anonymous-merge event).
 *
 * An `anon_merge` log *is* the merge, not an activity that came in through one, so it doesn't
 * count. Shared by the Activity Type cell's merge icon and the row-expansion analytics payload —
 * the two report the same claim, so they must not drift apart.
 */
export function isAnonymousMergeEvent(log: ActivityLog): boolean {
  if (log.type === LOG_TYPE.AnonMerge) return false;
  return (log.sources ?? []).some((s) => !isEmptyString(s.anonymous_id ?? ''));
}

function resolveDeliveryTypeName(deliveryType: string | null | undefined): string {
  if (deliveryType == null || deliveryType === '') {
    return 'action';
  }
  return DELIVERY_TYPE_NAMES[deliveryType] ?? 'action';
}

/**
 * Human label for a log's type. Applies the taxonomy-dependent overrides, then swaps the word
 * "Action" for the channel-specific delivery name (e.g. "Sent Action" → "Sent Email"). Falls
 * back to the raw parsed type when unmapped.
 */
export function activityLogTypeLabel(log: ActivityLog, terms: ActivityLogTypeTerms): string {
  const parsed = parseLogType(log.type);

  const overrides: Record<string, string> = {
    failed_object_journeys: `Failed journeys for object/relationship ${terms.campaignSingular}`,
    '_o:*:add_relationship': `Relationship created (to ${terms.personSingular})`,
    '_o:*:delete_relationship': `Relationship deleted (to ${terms.personSingular})`,
    profile_create: `${terms.personSingular} created`,
    profile_delete: `${terms.personSingular} deleted`,
    profile_merge: `${terms.personPlural} merged`,
  };

  if (parsed === 'sent_action' && log.delivery_type === 'live_notification') {
    const operation = isRecord(log.attrs) ? log.attrs.operation : undefined;
    const operationLabel = typeof operation === 'string' ? LIVE_NOTIFICATION_OPERATION_LABELS[operation] : undefined;
    if (operationLabel != null) {
      return operationLabel;
    }
  }

  // Channel-native types (sent_sms, delivered_line, …) resolve from the channel map; their
  // labels already name the channel, so the "Action" swap below is a no-op for them.
  const name = overrides[parsed] ?? LOG_TYPE_NAMES[parsed] ?? CHANNEL_LOG_TYPE_NAMES[parsed];
  if (name == null) {
    return parsed;
  }
  return name.replace(/\bAction\b/, resolveDeliveryTypeName(log.delivery_type));
}

export interface ActivityTypeOption {
  value: string;
  label: string;
}

/** A collapsible option group for the Activity Type Select (Pluma grouped-options shape). */
export interface ActivityTypeOptionGroup {
  label: string;
  options: ActivityTypeOption[];
  isCollapsible: boolean;
  defaultIsCollapsed: boolean;
}

// Channel suffixes + action prefixes used to bucket `action_channel` types into dropdown groups.
const CHANNEL_NAMES = new Set([
  'email',
  'in_app',
  'inbox',
  'line',
  'live_notification',
  'push',
  'slack',
  'sms',
  'twilio',
  'urban_airship',
  'webhook',
  'whatsapp',
]);
const DELIVERY_ACTIONS = new Set(['attempted', 'sent', 'delivered', 'drafted', 'deferred']);
const ENGAGEMENT_ACTIONS = new Set(['clicked', 'opened', 'converted', 'replied']);
const SUPPRESSION_ACTIONS = new Set(['bounced', 'dropped', 'undeliverable', 'unsubscribed', 'failed']);

const GROUP_ORDER = [
  'Delivery Actions',
  'Engagement Actions',
  'Suppression Actions',
  'Profile & Identity',
  'Relationships',
  'Behavioral Events',
  'System Operations',
  'Other',
] as const;

/** Exact type → group. Built from per-group lists so the mapping reads as data, not branches. */
const GROUP_BY_EXACT_TYPE: Record<string, string> = Object.fromEntries(
  (
    [
      [
        'Profile & Identity',
        [
          'anon_merge',
          'attribute_change',
          'failed_attribute_change',
          'device_change',
          'profile_merge',
          'profile_create',
          'profile_delete',
        ],
      ],
      [
        'Behavioral Events',
        [
          'event',
          'failed_event',
          'clicked_content',
          'converted_content',
          'viewed_content',
          'page',
          'screen',
          'geofence',
        ],
      ],
      [
        'System Operations',
        [
          'segment_build',
          'skipped_update',
          'failed_batch_update',
          'failed_query_collection',
          'failed_query_objects',
          'inbound',
          'webhook_event',
        ],
      ],
      ['Suppression Actions', ['spammed_email', 'spammed_action', 'topic_unsubscribed_email']],
    ] as const
  ).flatMap(([group, types]) => types.map((type) => [type, group])),
);

/** `action_channel` (e.g. `delivered_email`) → its action group, or null if not a channel action. */
function channelActionGroup(v: string): string | null {
  const underscore = v.indexOf('_');
  if (underscore <= 0 || underscore >= v.length - 1) return null;
  const action = v.slice(0, underscore);
  if (!CHANNEL_NAMES.has(v.slice(underscore + 1))) return null;
  if (DELIVERY_ACTIONS.has(action)) return 'Delivery Actions';
  if (ENGAGEMENT_ACTIONS.has(action)) return 'Engagement Actions';
  if (SUPPRESSION_ACTIONS.has(action)) return 'Suppression Actions';
  return null;
}

/** Which dropdown group a type belongs to. */
function groupForType(value: string): string {
  const v = value.toLowerCase();

  // Relationship patterns win over the exact-type table (no exact-table type also matches these
  // substrings, so the precedence is only defensive).
  if (v.includes('relationship') || v.includes('object_journeys')) return 'Relationships';
  if (v.startsWith('_o:') || v.startsWith('_r:')) return 'Profile & Identity';

  return GROUP_BY_EXACT_TYPE[v] ?? channelActionGroup(v) ?? 'Other';
}

/** The two channels that both label their types "SMS": legacy Twilio and custom SMS. */
const SMS_CHANNELS = ['twilio', 'sms'] as const;
type SmsChannel = (typeof SMS_CHANNELS)[number];

/** The SMS channel a log type belongs to, or null if it isn't an SMS type. */
function smsChannelForLogType(type: string): SmsChannel | null {
  return SMS_CHANNELS.find((channel) => type.endsWith(`_${channel}`)) ?? null;
}

/**
 * The SMS channels the filter offers: every one in `enabled_actions`, plus the channel named by the
 * current filter. Hiding a channel would strand the logs it already produced, so any channel a
 * workspace can have logs from keeps its types.
 *
 * With neither provisioned, offer legacy Twilio. `enabled_actions` drops `twilio` once its
 * credentials are deactivated, so a workspace that moved off it can still reach its old logs.
 *
 * Mirrors the Ember `cio-activity-log-filter` (MESS-1200). Both surfaces must offer the same types.
 */
function visibleSmsChannels(enabledActions: readonly string[], currentType: string): SmsChannel[] {
  const channels = SMS_CHANNELS.filter((channel) => enabledActions.includes(channel));

  const currentSmsChannel = smsChannelForLogType(currentType);
  if (currentSmsChannel != null && !channels.includes(currentSmsChannel)) {
    return [...channels, currentSmsChannel];
  }

  return channels.length > 0 ? channels : ['twilio'];
}

export interface ActivityTypeOptionsAvailability {
  /** Whether the account has the geofences plan entitlement. */
  isGeofenceAvailable?: boolean;
  /** Whether consent records are on: the consent_records flag plus the account's own setting. */
  isConsentAvailable?: boolean;
  /** Whether the query-objects-action flag is on. Without it no Query Objects step can run, so none can fail. */
  isQueryObjectsAvailable?: boolean;
  /** Whether the account has the live_notifications plan entitlement. */
  isLiveNotificationsAvailable?: boolean;
  /** Raw `enabled_actions`. Decides which SMS channels are offered. */
  enabledActions?: readonly string[];
  /** The filter's current value. Its SMS channel stays offered. */
  currentType?: string;
}

/**
 * Grouped, collapsible options for the Activity Type filter Select — the standard types plus the
 * channel-native types, each bucketed into its category. The Geofence, live-notification, and
 * Consent record options only appear for entitled accounts, and the failed object query option only
 * with its feature flag.
 *
 * Deferred: object/relationship wildcard types (`_o:*:` / `_r:*:`) are excluded (they need
 * per-object-type expansion), and channel types aren't yet feature-flag-gated (Builder-plan /
 * disabled-channel hiding).
 */
export function buildActivityTypeOptions(
  terms: ActivityLogTypeTerms,
  {
    isGeofenceAvailable = false,
    isLiveNotificationsAvailable = false,
    isConsentAvailable = false,
    isQueryObjectsAvailable = false,
    enabledActions = [],
    currentType = '',
  }: ActivityTypeOptionsAvailability = {},
): ActivityTypeOptionGroup[] {
  const overrides: Record<string, string> = {
    profile_create: `${terms.personSingular} created`,
    profile_delete: `${terms.personSingular} deleted`,
    profile_merge: `${terms.personPlural} merged`,
    failed_object_journeys: `Failed journeys for object/relationship ${terms.campaignSingular}`,
  };
  const labelFor = (value: string) =>
    overrides[value] ?? LOG_TYPE_NAMES[value] ?? CHANNEL_LOG_TYPE_NAMES[value] ?? value;

  // `*_twilio` and `*_sms` share their labels ("Delivered SMS"), so only the channels a workspace
  // can have logs from are offered. Rows of either type still get labels from CHANNEL_LOG_TYPE_NAMES.
  const offeredSmsChannels = visibleSmsChannels(enabledActions, currentType);
  const isHiddenSmsType = (value: string) => {
    const channel = smsChannelForLogType(value);
    return channel != null && !offeredSmsChannels.includes(channel);
  };

  const values = [...new Set([...Object.keys(LOG_TYPE_NAMES), ...Object.keys(CHANNEL_LOG_TYPE_NAMES)])].filter(
    (value) => !value.startsWith('_o:') && !value.startsWith('_r:') && !isHiddenSmsType(value),
  );
  const availableValues = values.filter(
    (value) =>
      (value !== LOG_TYPE.Geofence || isGeofenceAvailable) &&
      (value !== LOG_TYPE.Consent || isConsentAvailable) &&
      (value !== LOG_TYPE.FailedQueryObjects || isQueryObjectsAvailable) &&
      (!value.endsWith('_live_notification') || isLiveNotificationsAvailable),
  );

  return toOptionGroups(availableValues.map((value) => ({ type: value, option: { value, label: labelFor(value) } })));
}

type TypedOption = {
  /** The wildcard type, which decides the group. */
  type: string;
  option: ActivityTypeOption;
};

/** Bucket options into their collapsible dropdown groups, each sorted by label, groups by name. */
function toOptionGroups(entries: readonly TypedOption[]): ActivityTypeOptionGroup[] {
  const groups = new Map<string, ActivityTypeOption[]>(GROUP_ORDER.map((name) => [name, []]));
  for (const { type, option } of entries) {
    groups.get(groupForType(type))?.push(option);
  }

  return [...groups.entries()]
    .filter(([, options]) => options.length > 0)
    .map(([label, options]) => ({
      label,
      options: options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
      isCollapsible: true,
      defaultIsCollapsed: true,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The object type an object's own activity filter is pinned to. */
export interface ActivityObjectType {
  id: string;
  /** The type's singular name ("Company"), which replaces "Object" in the labels. */
  singularName: string;
}

/**
 * The Activity Type options on one object's own activity: the object and relationship-attribute
 * types, each pinned to that object's type (`_o:*:create` → `_o:7:create`), without "Object
 * deleted" — a deleted object has no page to read it on. Ported from Ember's `object.activity`
 * controller (`hiddenTypes`) and `createLogTypeObjectsWithObjectTypes`, which name each type after
 * the object type ("Company attribute change", "Company relationship created (to Person)").
 */
export function buildObjectActivityTypeOptions(
  terms: ActivityLogTypeTerms,
  objectType: ActivityObjectType,
): ActivityTypeOptionGroup[] {
  const values = [...LOG_TYPES_FOR_OBJECTS, ...LOG_TYPES_FOR_RELATIONSHIPS_ATTRIBUTES].filter(
    (value) => value !== LOG_TYPE.ObjectDelete,
  );
  const labelFor = (value: string) => {
    if (value === LOG_TYPE.ObjectAddRelationship) {
      return `${objectType.singularName} relationship created (to ${terms.personSingular})`;
    }
    if (value === LOG_TYPE.ObjectDeleteRelationship) {
      return `${objectType.singularName} relationship deleted (to ${terms.personSingular})`;
    }
    const name = LOG_TYPE_NAMES[value] ?? value;
    return value.startsWith('_o:') ? name.replace(/^Object/, objectType.singularName) : name;
  };

  return toOptionGroups(
    values.map((value) => ({
      type: value,
      option: { value: logTypeForObjectType(value, objectType.id), label: labelFor(value) },
    })),
  );
}

/** Channels a Builder account cannot send through, so their logs never apply (Ember `BUILDER_UNAVAILABLE_CHANNEL_LOG_TYPES`). */
const BUILDER_UNAVAILABLE_CHANNELS = ['line', 'slack', 'sms', 'twilio', 'whatsapp'];

/** Log types for features a Builder account does not have, beyond objects and relationships. */
const BUILDER_UNAVAILABLE_LOG_TYPES = new Set<string>([
  LOG_TYPE.FailedLlmAction,
  LOG_TYPE.FailedQueryObjects,
  'failed_query_collection',
]);

/**
 * Whether a log is about an object or a relationship: the `_o:` and `_r:` namespaces, plus the
 * person-side relationship logs (Ember `isObjectOrRelationshipLogType`).
 */
function isObjectOrRelationshipLogType(type: string): boolean {
  return (
    type.startsWith('_o:') ||
    type.startsWith('_r:') ||
    type === 'add_relationship' ||
    type === 'delete_relationship' ||
    type === 'failed_object_journeys'
  );
}

function isBuilderUnavailableChannelType(type: string): boolean {
  return (
    CHANNEL_LOG_TYPE_NAMES[type] != null && BUILDER_UNAVAILABLE_CHANNELS.some((channel) => type.endsWith(`_${channel}`))
  );
}

/**
 * Whether a Builder account sees this log type. Builder has no objects, relationships, AI actions,
 * collection or object queries, or non-email messaging channels, so a person's Activity tab hides
 * their logs from both the filter and the rows (Ember `BUILDER_HIDDEN_LOG_TYPES` and `visibleLogs`).
 */
export function isHiddenFromBuilder(type: string): boolean {
  return (
    isObjectOrRelationshipLogType(type) ||
    BUILDER_UNAVAILABLE_LOG_TYPES.has(type) ||
    isBuilderUnavailableChannelType(type)
  );
}

type PersonActivityTypeOptionsAvailability = ActivityTypeOptionsAvailability & { isBuilder: boolean };

/**
 * The Activity type options on a person's Activity tab: the workspace feed's options without
 * profile deletes, which never show on a profile, and without what a Builder account cannot see
 * (Ember `pods/person/activity/controller.ts` `hiddenTypes`).
 */
export function buildPersonActivityTypeOptions(
  terms: ActivityLogTypeTerms,
  { isBuilder, ...availability }: PersonActivityTypeOptionsAvailability,
): ActivityTypeOptionGroup[] {
  const isOffered = (value: string) => value !== LOG_TYPE.ProfileDelete && !(isBuilder && isHiddenFromBuilder(value));
  return buildActivityTypeOptions(terms, availability)
    .map((group) => ({ ...group, options: group.options.filter((option) => isOffered(option.value)) }))
    .filter((group) => group.options.length > 0);
}
